import { Button, Fieldset, Grid, GridItem, Text, VStack } from '@chakra-ui/react';
import { FormNumberInput } from '@components/cockpit/panels/systemParameters/SystemParametersPanel';
import { SystemParamFormFields } from '@components/cockpit/panels/systemParameters/systemStorage/SystemStorage';
import ErrorsContainer from '@components/common/form/ErrorsContainer';
import { Form } from '@components/ui/Form';
import { FormCheckbox } from '@components/ui/FormCheckbox';
import useValidation from '@hooks/useValidation';
import { useUpdateStorageConfigurationMutation } from '@services/c2/c2.api';
import { convertDuration } from '@utils/date.utils';
import { systemStorageSchema } from '@validations/system.storage.schema';
import { Formik } from 'formik';
import { FormattedMessage } from 'react-intl';

import { C2StorageConfiguration } from '@/types/c2/c2.types';

type SystemStorageFormProps = {
  storageConfiguration: C2StorageConfiguration | undefined;
  isLoadingGet: boolean;
  cachedValues: SystemParamFormFields;
};

export function SystemStorageForm({
  storageConfiguration,
  isLoadingGet,
  cachedValues,
}: Readonly<SystemStorageFormProps>) {
  const validationSchema = useValidation(systemStorageSchema);

  const [patchStorageConfiguration, { isLoading: isLoadingPatch }] = useUpdateStorageConfigurationMutation();

  const isLoading = isLoadingGet || isLoadingPatch;

  function submit(values: SystemParamFormFields) {
    if (
      storageConfiguration?.site &&
      storageConfiguration.replayConfiguration?.id &&
      storageConfiguration.sensorConfiguration?.id
    ) {
      patchStorageConfiguration({
        site: storageConfiguration.site,
        storageConfiguration: {
          ...storageConfiguration,
          replayConfiguration: {
            ...storageConfiguration.replayConfiguration,
            enabledSituationStorage: values.enabledSituationStorage,
            situationStorageDuration: convertDuration(values.situationStorageDuration, 'days', 'seconds'),
          },
          sensorConfiguration: {
            ...storageConfiguration.sensorConfiguration,
            enabledSensorRawStorage: values.enabledSensorRawStorage,
            sensorRawStorageDuration: convertDuration(values.sensorRawStorageDuration, 'days', 'seconds'),
          },
          videoConfiguration: {
            ...storageConfiguration.videoConfiguration,
            enabledLadVideoStreamStorage: values.enabledLadVideoStreamStorage,
            ladVideoStreamStorageDuration: values.ladVideoStreamStorageDuration,
          },
        },
      });
    }
  }
  return (
    <Formik
      initialValues={cachedValues}
      enableReinitialize={true}
      validationSchema={validationSchema}
      onSubmit={submit}
    >
      {({ touched, errors, values, dirty, isValid }) => (
        <Form>
          <Fieldset.Root invalid={Object.keys(errors).some((key) => touched[key as keyof typeof touched])}>
            <VStack gap={5}>
              <Grid templateColumns="repeat(2,1fr)" templateRows="repeat(2,1fr)" gap={5}>
                <GridItem width="251px" display="flex" flexDirection="column" gap={1.5}>
                  <Text color="neutral.300">
                    <FormattedMessage id="components.systemParameters.sensorRawStorage" />
                  </Text>
                  <FormCheckbox
                    name="enabledSensorRawStorage"
                    label={
                      <Text>
                        <FormattedMessage id="components.systemParameters.enableStorage" />
                      </Text>
                    }
                  />
                  <FormNumberInput
                    name="sensorRawStorageDuration"
                    width="100%"
                    height="48px"
                    disabled={!values.enabledSensorRawStorage}
                    unit="day"
                  />
                </GridItem>

                <GridItem width="251px" display="flex" flexDirection="column" gap={1.5}>
                  <Text color="neutral.300">
                    <FormattedMessage id="components.systemParameters.situationStorage" />
                  </Text>
                  <FormCheckbox
                    name="enabledSituationStorage"
                    label={
                      <Text>
                        <FormattedMessage id="components.systemParameters.enableStorage" />
                      </Text>
                    }
                  />
                  <FormNumberInput
                    name="situationStorageDuration"
                    width="100%"
                    height="48px"
                    disabled={!values.enabledSituationStorage}
                    unit="day"
                  />
                </GridItem>

                <GridItem width="251px" display="flex" flexDirection="column" gap={1.5}>
                  <Text color="neutral.300">
                    <FormattedMessage id="components.systemParameters.ladVideoStreamStorage" />
                  </Text>
                  <FormCheckbox
                    name="enabledLadVideoStreamStorage"
                    label={
                      <Text>
                        <FormattedMessage id="components.systemParameters.enableStorage" />
                      </Text>
                    }
                  />
                  <FormNumberInput
                    name="ladVideoStreamStorageDuration"
                    width="100%"
                    height="48px"
                    disabled={!values.enabledLadVideoStreamStorage}
                    unit="hour"
                  />
                </GridItem>
              </Grid>
              <Fieldset.ErrorText alignSelf="flex-start" margin={0} width="100%">
                <ErrorsContainer touched={touched} errors={errors} />
              </Fieldset.ErrorText>
              <Button
                type="submit"
                disabled={
                  !dirty ||
                  !isValid ||
                  isLoading ||
                  !(
                    storageConfiguration?.site &&
                    storageConfiguration.replayConfiguration?.id &&
                    storageConfiguration.sensorConfiguration?.id
                  )
                }
                loading={isLoading}
                variant="primary"
                marginTop="auto"
              >
                <Text>
                  <FormattedMessage id="components.systemParameters.UPDATE" />
                </Text>
              </Button>
            </VStack>
          </Fieldset.Root>
        </Form>
      )}
    </Formik>
  );
}
